import { CommonModule } from '@angular/common';
import { NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, SimpleChanges, TemplateRef, inject, input, model, output, signal } from '@angular/core';

import { TranslateModule } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';

import { InputUploadValue } from '@camelot/form-model';
import { FontIconComponent } from '@camelot/icons';
import { Menu, MenuIcon, NavigationComponent } from '@camelot/menu';
import {
  ButtonComponent,
  ButtonToolComponent,
  ErrorComponent,
  ExpandableTextComponent,
  LayoutFullPanelComponent,
  LinkComponent,
  LoaderComponent,
  TextComponent,
} from '@camelot/ui';
import { CamUsersService } from '@camelot/user';
import { CamBaseComponent, ModalState, fullName } from '@camelot/utils';
import { BlockTextComponent, EditorInputComponent, EditorInputSavedData, WysiswgBlockData } from '@camelot/wysiswyg';

import { CamCommunicationsService } from '../../services/communications.service';
import { Communication, CommunicationParticipantExtended, CommunicationType } from '../../services/dto/communication';
import { SignatureExtended } from '../../services/dto/signature';
import { TemplateVariant } from '../../services/dto/template';
import { CamSharedCommunicationsService } from '../../services/shared.service';
import { CamCommunicationsTemplatesService } from '../../services/templates.service';
import { CamTranslationCommunication } from '../../translation.service';
import { ConversationAttachments } from '../conversation-attachments/conversation-attachments.component';
import { ResponseToCommunicationComponent } from '../message-container/message-mail/response-to-communication/response-to-communication.component';
import { CommunicationTemplateChoiceComponent } from '../templates/choice/choice.component';
import { MessageBoxAttachmentsComponent } from './attachments/attachments.component';
import { CorrespondentInput, MessageBoxInputComponent, OutputCorrespondentInput } from './input/input.component';
import { MessageBoxToolbarComponent } from './toolbar/toolbar.component';

export type MessageBoxOutputMessage = {
  type: CommunicationType;
  blocks: WysiswgBlockData[];
  tos: CorrespondentInput[];
  ccs: CorrespondentInput[];
  tags: CorrespondentInput[];
  attachments: InputUploadValue[];
};

@Component({
  imports: [
    BlockTextComponent,
    ButtonComponent,
    ButtonToolComponent,
    CommonModule,
    CommunicationTemplateChoiceComponent,
    EditorInputComponent,
    ErrorComponent,
    ExpandableTextComponent,
    FontIconComponent,
    LayoutFullPanelComponent,
    LinkComponent,
    LoaderComponent,
    MessageBoxAttachmentsComponent,
    MessageBoxInputComponent,
    MessageBoxToolbarComponent,
    NavigationComponent,
    NgTemplateOutlet,
    ResponseToCommunicationComponent,
    TextComponent,
    TranslateModule,
  ],
  selector: 'cam-communication-message-box-container',
  styleUrls: ['./message-box-container.component.scss'],
  templateUrl: './message-box-container.component.html',
})
export class MessageBoxContainerComponent extends CamBaseComponent implements OnInit {
  conversationParticipants = input.required<CommunicationParticipantExtended[]>();
  replyTo = model<{ communication: Communication; isForward: boolean } | null>(null);
  clear$ = input<Observable<void>>();
  statusTemplate = input.required<TemplateRef<any>>();
  taskId = input.required<string>();
  openPanel = input(false);
  isChatEnabled = input(false);
  isMailEnabled = input(true);

  message = output<MessageBoxOutputMessage>();

  toggleExpanded = output();

  readonly usersService = inject(CamUsersService);
  readonly users = signal<{ id: string; name: string }[]>([]);
  readonly communicationsTemplateService = inject(CamCommunicationsTemplatesService);

  readonly CommunicationType = CommunicationType;
  readonly requestMessage$ = new Subject<void>();
  readonly openAttachments$ = new Subject<void>();
  readonly selectedTemplateVariantBodyChanged$ = new Subject<{
    blocks: WysiswgBlockData[] | string;
    saveAfter?: boolean;
  }>();

  readonly messageContent = new Subject<{ blocks: WysiswgBlockData[] }>();
  public templateChoiceModal = new ModalState<CommunicationType, TemplateVariant>();
  public collapsed: boolean = true;
  public withSignature: boolean = false;

  public attachments = signal<InputUploadValue[]>([]);
  public correspondents: OutputCorrespondentInput | null = null;
  public activeTab: CommunicationType = CommunicationType.Mail;
  public selectedTemplateVariant: TemplateVariant | null = null;

  public menu = new Menu({
    direction: 'horizontal',
    elements: [
      new MenuIcon({
        callback: () => this.changeTab(CommunicationType.Mail),
        defaultOpen: true,
        icon: 'mail',
        key: CommunicationType.Mail.toString(),
        label: 'communication.message.tab.mail',
        order: 1,
      }),
      new MenuIcon({
        callback: () => this.changeTab(CommunicationType.Chat),
        disabled: !this.isChatEnabled(),
        icon: 'message',
        key: CommunicationType.Chat.toString(),
        label: 'communication.message.tab.chat',
        order: 2,
      }),
      new MenuIcon({
        callback: () => this.changeTab(CommunicationType.Intern),
        icon: 'eye-closed',
        key: CommunicationType.Intern.toString(),
        label: 'communication.message.tab.intern',
        order: 3,
      }),
    ],
  });

  public activateMenuItem$ = new Subject<string>();

  private readonly _sharedCommunicationService = inject(CamSharedCommunicationsService);
  private readonly _communicationService = inject(CamCommunicationsService);

  public readonly mySignature$ = signal<Observable<SignatureExtended | null> | null>(null);

  documentsIds() {
    return this._sharedCommunicationService.documentsIds(this.taskId());
  }

  attachmentsConfig(): ConversationAttachments {
    return {
      documentsIds: this.documentsIds(),
      open$: this.openAttachments$,
      preSet: this.selectedTemplateVariant?.documentIds ?? [],
      value: this.attachments(),
    };
  }

  constructor() {
    super();
    CamTranslationCommunication.getInstance();
    this.usersService.fetchUsers$().subscribe({
      next: users => this.users.set(users.map(user => ({ id: user.id, name: fullName(user) }))),
    });
    this._fetch();
    this.mySignature$.set(this._communicationService.mySignature.get$());
  }

  ngOnInit() {
    const clear$ = this.clear$();
    if (clear$) {
      this._registerSubscription(clear$.subscribe({ next: () => this.attachments.set([]) }));
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['replyTo'] && changes['replyTo'].currentValue) {
      this.collapsed = false;
    }
    if (changes['openPanel'] && changes['openPanel'].currentValue) {
      this.collapsed = false;
    }
    if (changes['isChatEnabled']) {
      const chatItem = this.menu.elements.find(e => e.key === CommunicationType.Chat.toString());
      if (chatItem) {
        chatItem.disabled = !this.isChatEnabled();
      }
    }
    if (changes['isMailEnabled']) {
      const mailItem = this.menu.elements.find(e => e.key === CommunicationType.Mail.toString());
      if (mailItem) {
        mailItem.disabled = !this.isMailEnabled();
      }
      if (!this.isMailEnabled() && this.activeTab === CommunicationType.Mail) {
        this.changeTab(CommunicationType.Intern);
        this.activateMenuItem$.next(CommunicationType.Intern.toString());
      }
    }
  }

  public changeTab(tab: CommunicationType) {
    this.activeTab = tab;
  }

  public updateCorrespondents(correspondents: OutputCorrespondentInput) {
    this.correspondents = correspondents;
  }

  public updateAttachments(attachments: InputUploadValue[]) {
    this.attachments.set(attachments);
  }

  public removeAttachment(attachment: InputUploadValue) {
    this.attachments.update(values => values.filter(value => value.id !== attachment.id));
  }
  public send(data: EditorInputSavedData, signature: SignatureExtended | null) {
    this.message.emit({
      attachments: this.attachments(),
      blocks: this.withSignature ? data.blocks.concat(signature?.blocks ?? []) : data.blocks,
      ccs: this.correspondents?.ccs ?? [],
      tags: data.tags.map(tag => ({ userId: tag })) ?? [],
      tos: this.correspondents?.tos ?? [],
      type: this.activeTab,
    });
  }

  public toogleCollapsed() {
    this.collapsed = !this.collapsed;
  }

  public selectTemplateVariant(variant: TemplateVariant | null) {
    this.selectedTemplateVariant = variant;
    if (variant?.jsonBodyParsed) {
      this.communicationsTemplateService
        .previewConversationTemplate$(JSON.stringify(variant.jsonBodyParsed), [
          { key: 'TASK_ID', value: this.taskId() },
        ])
        .subscribe({
          next: value =>
            this.selectedTemplateVariantBodyChanged$.next({
              blocks: JSON.parse(value),
            }),
        });
    }
  }
  public toggleTemplateChoice() {
    if (this.templateChoiceModal.open()) {
      this.templateChoiceModal.open.set(false);
    } else {
      this.templateChoiceModal.asked(this.activeTab);
    }
  }

  private _fetch() {
    this.requestState.asked();
    this._communicationService.fetchMySignature$().subscribe({
      complete: () => this.requestState.completed(),
      error: (error: HttpErrorResponse) => {
        this.requestState.onError(error.status, error.statusText);
      },
    });
  }
}
